/**
 * MergeGeometryNode - Combines multiple geometries into one
 *
 * SceneNode calls mergeFromGeometries() with upstream geometry array.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { BufferGeometry, Three } from '../types';
import type { ThreeDependencies } from '../threejsPlugin';
import { mergeGeometries } from '../geometryUtils';

const mergeEndpoints = {
  ...internals({
    meshIn: { dataType: 'three:group', isInput: true, label: 'Mesh/Geometry' },
    geometryOut: { dataType: 'three:geometry', isOutput: true }
  }),
  ...params({
    weldTolerance: { value: 0, min: 0, max: 0.1, step: 0.001, label: 'Weld Tolerance' }
  })
};

class MergeGeometryNode extends BaseNode<typeof mergeEndpoints> {
  three: Three;
  /** Callback to request scene rebuild */
  private rebuildCallback: (() => void) | null = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  /** Merge geometries — called by SceneNode with upstream geometry array */
  mergeFromGeometries(geometries: BufferGeometry[]): BufferGeometry | null {
    const tolerance = this.params.weldTolerance.value;
    return mergeGeometries(geometries, tolerance);
  }

  /** Store rebuild callback from SceneNode */
  setRebuildCallback(callback: () => void): void {
    this.rebuildCallback = callback;
  }

  onParamChanged(): void {
    this.rebuildCallback?.();
  }
}

export const mergeGeometryNodeDef: NodeDef<MergeGeometryNode> = {
  type: 'MergeGeometry',
  shortName: 'merge',
  info: 'Combines multiple geometries into a single buffer geometry with optional vertex welding.',
  category: 'geometry',
  endpoints: mergeEndpoints,
  ctor: MergeGeometryNode
};
